import type { PoseData, PersonID, PersonData } from '../../types';

/**
 * PersonMatcher handles matching detected poses to existing tracked people.
 * Uses shoulder centroid distance to determine if a pose belongs to an existing person.
 * 
 * Matching Algorithm:
 * 1. Calculate shoulder centroid for each detected pose
 * 2. Calculate shoulder centroid for each existing person
 * 3. For each detected pose, find closest existing person by centroid distance
 * 4. If distance < threshold, match them
 * 5. If distance >= threshold, pose is unmatched (new person)
 * 
 * Validates Requirements 2.1, 2.4, 2.5
 */
export class PersonMatcher {
  private readonly matchingThreshold: number;

  /**
   * Create a PersonMatcher with specified distance threshold.
   * @param matchingThreshold Distance threshold in normalized coordinates (default: 0.15)
   */
  constructor(matchingThreshold: number = 0.15) {
    this.matchingThreshold = matchingThreshold;
  }

  /**
   * Calculate the centroid (center point) of shoulder landmarks.
   * Uses the midpoint between left and right shoulders.
   * 
   * @param pose Pose data containing shoulder landmarks
   * @returns Centroid coordinates {x, y} in normalized space [0, 1]
   * 
   * Validates Requirement 2.4: System SHALL use centroid of shoulder landmarks
   */
  calculateShoulderCentroid(pose: PoseData): { x: number; y: number } {
    const leftShoulder = pose.landmarks.leftShoulder;
    const rightShoulder = pose.landmarks.rightShoulder;

    return {
      x: (leftShoulder.x + rightShoulder.x) / 2,
      y: (leftShoulder.y + rightShoulder.y) / 2,
    };
  }

  /**
   * Calculate Euclidean distance between two points.
   * 
   * @param p1 First point with x, y coordinates
   * @param p2 Second point with x, y coordinates
   * @returns Euclidean distance between the points
   */
  euclideanDistance(
    p1: { x: number; y: number },
    p2: { x: number; y: number }
  ): number {
    const dx = p1.x - p2.x;
    const dy = p1.y - p2.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Match detected poses to existing people based on shoulder centroid proximity.
   * 
   * Algorithm:
   * - For each detected pose, calculate shoulder centroid
   * - Find the closest existing person by centroid distance
   * - If distance < threshold, match them
   * - If distance >= threshold, pose is unmatched (new person)
   * 
   * @param detectedPoses Array of newly detected poses
   * @param existingPeople Map of currently tracked people
   * @returns Object with matched pairs and unmatched poses
   * 
   * Validates Requirements 2.1, 2.5: System SHALL match poses to existing Person_IDs
   * based on landmark proximity with threshold of 0.15
   */
  matchPoses(
    detectedPoses: PoseData[],
    existingPeople: Map<PersonID, PersonData>
  ): {
    matches: Array<{ pose: PoseData; personId: PersonID; distance: number }>;
    unmatchedPoses: PoseData[];
    unmatchedPeople: PersonID[];
  } {
    const matches: Array<{ pose: PoseData; personId: PersonID; distance: number }> = [];
    const unmatchedPoses: PoseData[] = [];
    const matchedPersonIds = new Set<PersonID>();

    // For each detected pose, find the closest existing person
    for (const pose of detectedPoses) {
      const poseCentroid = this.calculateShoulderCentroid(pose);
      
      let closestPersonId: PersonID | null = null;
      let closestDistance = Infinity;

      // Find closest existing person
      for (const [personId, personData] of existingPeople.entries()) {
        // Skip people already matched in this frame
        if (matchedPersonIds.has(personId)) {
          continue;
        }

        const personCentroid = this.calculateShoulderCentroid(personData.poseData);
        const distance = this.euclideanDistance(poseCentroid, personCentroid);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestPersonId = personId;
        }
      }

      // Check if closest match is within threshold
      if (closestPersonId !== null && closestDistance < this.matchingThreshold) {
        matches.push({
          pose,
          personId: closestPersonId,
          distance: closestDistance,
        });
        matchedPersonIds.add(closestPersonId);
      } else {
        // No match found within threshold - this is a new person
        unmatchedPoses.push(pose);
      }
    }

    // Find people that weren't matched to any pose
    const unmatchedPeople: PersonID[] = [];
    for (const personId of existingPeople.keys()) {
      if (!matchedPersonIds.has(personId)) {
        unmatchedPeople.push(personId);
      }
    }

    return {
      matches,
      unmatchedPoses,
      unmatchedPeople,
    };
  }

  /**
   * Get the current matching threshold.
   * @returns Distance threshold in normalized coordinates
   */
  getThreshold(): number {
    return this.matchingThreshold;
  }
}
